import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    CircularProgress,
    Typography,
    Alert,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import SortableImageItem from './SortableImageItem';
import ImageDragPreview from './ImageDragPreview';

const useStyles = makeStyles(() => ({
    dialog: {
        '& .MuiDialog-paper': {
            maxHeight: '90vh',
        },
    },
    dialogTitle: {
        fontFamily: "'GucciSans-Bold' !important",
        fontSize: '18px',
        fontWeight: 'bold',
        backgroundColor: '#f5f5f5',
    },
    dialogContent: {
        padding: '20px !important',
        minHeight: '400px',
        maxHeight: 'calc(90vh - 200px)',
        overflowY: 'auto',
    },
    imagesList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    dragOverlayImage: {
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
    },
    button: {
        backgroundColor: '#000000 !important',
        color: 'white !important',
        boxShadow: '2px 2px #6f6f6f6b !important',
        fontFamily: "'GucciSans-Bold' !important",
        '&:hover': {
            backgroundColor: '#333333 !important',
        },
        '&:disabled': {
            backgroundColor: '#999696bf !important',
        },
    },
    emptyState: {
        textAlign: 'center',
        padding: '40px 20px',
    },
}));

export default function ImageOrderDialog(props) {
    const classes = useStyles();
    const { open, onClose, images, selectedEvent, onOrderUpdated, getAuthHeader } = props;

    const [orderedImages, setOrderedImages] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isModified, setIsModified] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            distance: 8,
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        if (open && images.length > 0) {
            const sorted = [...images].sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0));
            setOrderedImages(sorted);
            setIsModified(false);
            setError(null);
        }
    }, [open, images]);

    function handleDragStart(event) {
        setActiveId(event.active.id);
    }

    function handleDragEnd(event) {
        const { active, over } = event;
        setActiveId(null);

        if (over && active.id !== over.id) {
            const oldIndex = orderedImages.findIndex(img => img.id === active.id);
            const newIndex = orderedImages.findIndex(img => img.id === over.id);

            const newOrder = arrayMove(orderedImages, oldIndex, newIndex);
            setOrderedImages(newOrder);
            setIsModified(true);
        }
    }

    async function handleSaveOrder() {
        setLoading(true);
        setError(null);

        try {
            const authHeader = await getAuthHeader();
            const updates = orderedImages.map((img) => img.id);

            const response = await fetch(
                `${process.env.REACT_APP_API_ENDPOINT}photostudio/sort`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: authHeader,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        eventID: selectedEvent,
                        updates,
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            setIsModified(false);
            onOrderUpdated();
            onClose();
        } catch (err) {
            console.error('Errore salvataggio ordine:', err);
            setError(err.message || 'Errore nel salvataggio dell\'ordine');
        } finally {
            setLoading(false);
        }
    }

    const activeImage = activeId ? orderedImages.find(img => img.id === activeId) : null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            className={classes.dialog}
        >
            <DialogTitle className={classes.dialogTitle}>
                Manage Images Order
            </DialogTitle>

            <DialogContent className={classes.dialogContent}>
                {error && (
                    <Alert severity="error" sx={{ marginBottom: '16px' }}>
                        {error}
                    </Alert>
                )}

                {orderedImages.length === 0 ? (
                    <Box className={classes.emptyState}>
                        <Typography color="textSecondary">
                            No images available for this event.
                        </Typography>
                    </Box>
                ) : (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={orderedImages.map(img => img.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            <Box className={classes.imagesList}>
                                {orderedImages.map((image, index) => (
                                    <SortableImageItem
                                        key={image.id}
                                        image={image}
                                        index={index}
                                    />
                                ))}
                            </Box>
                        </SortableContext>

                        <DragOverlay>
                            {activeImage ? (
                                <ImageDragPreview image={activeImage} />
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                )}
            </DialogContent>

            <DialogActions sx={{ padding: '16px', gap: '8px' }}>
                <Button
                    onClick={onClose}
                    className='template_button'
                    disabled={loading}
                >
                    Close
                </Button>
                <Button
                    onClick={handleSaveOrder}
                    className={classes.button}
                    disabled={!isModified || loading}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    {loading && <CircularProgress size={20} sx={{ color: 'white' }} />}
                    {loading ? 'Saving...' : 'Save Order'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
